import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { auth, db, googleProvider } from '../services/firebase';
import { User, onAuthStateChanged, signOut as fbSignOut, signInWithPopup, signInWithRedirect, signInWithCredential, getRedirectResult, GoogleAuthProvider } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, orderBy, limit, getDocs, getDoc } from 'firebase/firestore';
import { checkAndAwardDailyVisitor } from '../services/badgeService';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { Capacitor } from '@capacitor/core';

export type UserProfile = {
  displayName?: string;
  username?: string;
  avatar?: string;
  bio?: string;
  location?: string;
  website?: string;
  specialties?: string[];
  role?: string;
};

export type EventManagerStatus = 'pending' | 'approved' | 'rejected' | null;

export type EventManagerProfile = {
  id: string;
  status: EventManagerStatus;
  companyName: string;
  name: string;
  chatId?: string;
} | null;

export type AuthContextValue = {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  hydrated: boolean;
  isSubscriber: boolean;
  subscriberSince: Date | null;
  isAdmin: boolean;
  eventManagerProfile: EventManagerProfile;
  eventManagerStatus: EventManagerStatus;
  signOut: () => Promise<void>;
  signInWithGoogle: (useRedirect?: boolean) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Move useAuth hook definition before AuthProvider
function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export { useAuth };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [subscriberSince, setSubscriberSince] = useState<Date | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [eventManagerProfile, setEventManagerProfile] = useState<EventManagerProfile>(null);
  const [eventManagerStatus, setEventManagerStatus] = useState<EventManagerStatus>(null);
  // Keep latest plan info from profile for fallback when subscriptions listener is empty
  const profilePlanRef = useRef<{ isPrem: boolean; since: Date | null }>({ isPrem: false, since: null });

  // Safety timeout: ensure loading is always set to false after 5 seconds
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [loading]);

  // Handle Google Sign-In redirect result (for mobile/redirect flow)
  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          // User signed in via redirect
          const credential = GoogleAuthProvider.credentialFromResult(result);
          console.log('[Auth] Google redirect sign-in success:', {
            uid: result.user.uid,
            email: result.user.email,
            hasAccessToken: !!credential?.accessToken,
          });
        }
      })
      .catch((error) => {
        // Handle errors from redirect sign-in
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error('[Auth] Google redirect sign-in error:', { errorCode, errorMessage });
      });
  }, []);

  // Google Sign-In function
  const signInWithGoogle = async (): Promise<void> => {
    try {
      if (Capacitor.isNativePlatform()) {
        await GoogleSignIn.initialize({
          clientId: '910177744331-uc03tvfnftei997a0vsh1at4ecbdd319.apps.googleusercontent.com',
        });
        const result = await GoogleSignIn.signIn();
        if (result.idToken) {
          const credential = GoogleAuthProvider.credential(result.idToken);
          await signInWithCredential(auth, credential);
        }
      } else {
        await signInWithPopup(auth, googleProvider);
      }
    } catch (error: any) {
      console.error('[Auth] Google sign-in failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      // mark that we've received the first auth state from Firebase
      if (!hydrated) setHydrated(true);
      setUser(u);
      // Rely solely on Firebase Auth persistence settings; no custom localStorage expiry
      if (!u) {
        // Only clear profile when fully signed out
        setProfile(null);
        setIsSubscriber(false);
        setSubscriberSince(null);
        setLoading(false);
        return;
      }
      // Badge: track login streak and award Daily Visitor when streak >= 7
      try { await checkAndAwardDailyVisitor(u.uid); } catch {}
      // Check admin status and event manager status from user profile
      try {
        const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', u.uid)));
        const userData = userDoc.docs[0]?.data();
        if (userData?.role === 'admin') {
          setIsAdmin(true);
        }
        // Check event manager status from profile (avoids permission issues)
        if (userData?.eventManagerId) {
          const emDoc = await getDoc(doc(db, 'eventManagers', userData.eventManagerId));
          if (emDoc.exists()) {
            const emData = emDoc.data();
            
            // Find the chat ID for this event manager
            let chatId: string | undefined;
            try {
              const chatQuery = await getDocs(query(
                collection(db, 'chats'),
                where('eventManagerId', '==', userData.eventManagerId),
                where('type', '==', 'approval'),
                limit(1)
              ));
              if (!chatQuery.empty) {
                chatId = chatQuery.docs[0].id;
              }
            } catch {
              // Chat might not exist yet
            }
            
            setEventManagerProfile({
              id: emDoc.id,
              status: emData.status,
              companyName: emData.companyName,
              name: emData.name,
              chatId,
            });
            setEventManagerStatus(emData.status);
          }
        }
      } catch (e: any) {
        // Silently ignore permission errors - this is expected for non-admin users
        if (e?.code !== 'permission-denied') {
          console.error('Error checking user status', e);
        }
      }

      // Subscribe to the user's profile document for live updates
      const ref = doc(db, 'users', u.uid);
      const unsubDoc = onSnapshot(ref, (snap) => {
        const d = (snap.data() as any) || null;
        setProfile(d || null);
        setLoading(false);
        // Fallback: if subscriptions listener hasn't marked as subscriber, infer from user.planTitle === 'Premium'
        try {
          const planTitleRaw = (d?.planTitle ?? '').toString();
          const isPrem = planTitleRaw.trim().toLowerCase() === 'premium';
          const act = d?.planActivatedAt || d?.planActivated || d?.createdAt || d?.created_at;
          let since: Date | null = null;
          if (act && typeof act?.toDate === 'function') since = act.toDate();
          else if (typeof act === 'string' || typeof act === 'number') since = new Date(act);
          profilePlanRef.current = { isPrem, since: since && !isNaN(since.getTime()) ? since : null };
          // Only set when we currently don't have a subscriber signal
          if (isPrem) {
            setIsSubscriber(true);
            setSubscriberSince(profilePlanRef.current.since);
            // eslint-disable-next-line no-console
            console.log('[Auth] fallback subscriber from user.profile', { planTitle: planTitleRaw, isSubscriber: true });
          }
        } catch {}
      }, (err) => {
        // Silently handle permission errors - user can't read their profile
        if (err?.code !== 'permission-denied') {
          console.error('Profile listener error:', err);
        }
        setLoading(false);
      });

      // Subscribe to subscription status (earliest subscription by startDate)
      try {
        const qy = query(
          collection(db, 'subscriptions'),
          where('userId', '==', u.uid),
          orderBy('startDate', 'desc'),
          limit(1)
        );
        const unsubSub = onSnapshot(qy, (snap) => {
          if (!snap.empty) {
            const data: any = snap.docs[0].data();
            const planTitleRaw = (data?.planTitle ?? data?.plan ?? data?.title ?? '').toString();
            const planTitle = planTitleRaw.trim();
            const sd = data?.startDate;
            let dt: Date | null = null;
            if (sd && typeof sd.toDate === 'function') dt = sd.toDate();
            else if (typeof sd === 'string' || typeof sd === 'number') dt = new Date(sd);
            const isPrem = planTitle.toLowerCase() === 'premium';
            setIsSubscriber(isPrem);
            setSubscriberSince(isPrem && dt && !isNaN(dt.getTime()) ? dt : null);
            try {
              // eslint-disable-next-line no-console
              console.log('[Auth] subscription doc', { planTitle, isSubscriber: isPrem });
            } catch {}
          } else {
            // No subscription docs; fall back to latest profile plan info
            const fb = profilePlanRef.current;
            setIsSubscriber(!!fb.isPrem);
            setSubscriberSince(fb.isPrem ? fb.since : null);
          }
        }, (err) => {
          // Silently handle permission and index errors
          if (err?.code !== 'permission-denied' && !err?.message?.includes('requires an index')) {
            console.error('Subscription listener error:', err);
          }
          // On listener error, fall back to profile state rather than forcing false
          const fb = profilePlanRef.current;
          setIsSubscriber(!!fb.isPrem);
          setSubscriberSince(fb.isPrem ? fb.since : null);
        });

        // Return combined cleanup
        return () => { try { unsubDoc(); } catch {} try { unsubSub(); } catch {} };
      } catch {
        // If query building fails, still return user doc cleanup
        return () => { try { unsubDoc(); } catch {} };
      }
    });

    return () => unsub();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    profile,
    loading,
    hydrated,
    isSubscriber,
    subscriberSince,
    isAdmin,
    eventManagerProfile,
    eventManagerStatus,
    signOut: async () => {
      try {
        localStorage.removeItem('crevvi_session_exp');
        localStorage.removeItem('eventManagerToken');
        localStorage.removeItem('eventManagerUser');
      } catch {}
      await fbSignOut(auth);
    },
    signInWithGoogle,
  }), [user, profile, loading, hydrated, isSubscriber, subscriberSince, isAdmin, eventManagerProfile, eventManagerStatus, signInWithGoogle]);

  // Debug: log subscription state whenever it changes
  useEffect(() => {
    try {
      // Keep logs compact and informative
      // eslint-disable-next-line no-console
      console.log('[Auth] subscription state', {
        isSubscriber,
        subscriberSince: subscriberSince ? subscriberSince.toISOString() : null,
      });
    } catch {}
  }, [isSubscriber, subscriberSince]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
